import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { use, useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/UnitSummaryPageFilterSection';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { FieldFilter } from '@/api/DTO/field-option/fieldOption.interface';
import {
	useGetFieldOptionListQuery,
	useGetFieldOptionUnitListQuery,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import EditUnitDialog from './components/EditUnitDialog';
import { FormType } from '@/api/enum/formType.enum';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';
export default function UnitSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const { fieldId } = useParams();
	const { userInfo } = useGetUser();

	const defaultPropertyFilter: FieldFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<FieldFilter> = defaultPropertyFilter;
	const methods = useForm<FieldFilter>({
		defaultValues,
	});
	const transformedSearchParams: FieldFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionUnitListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as FieldFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'code',
			headerName: t('field.code'),
			flex: 20,
		},
		{
			field: 'displayName',
			headerName: t('field.districtEn'),
			flex: 20,
		},
		{
			field: 'displayNameZh',
			headerName: t('field.districtZh'),
			flex: 20,
		},
		{
			field: 'nameEn',
			headerName: t('field.unitEn'),
			flex: 20,
		},
		{
			field: 'nameZh',
			headerName: t('field.unitZh'),
			flex: 20,
		},
		{
			field: 'addressEn',
			headerName: t('field.addressEn'),
			flex: 20,
		},
		{
			field: 'addressZh',
			headerName: t('field.addressZh'),
			flex: 20,
		},
		{
			field: 'phone',
			headerName: t('field.telNo'),
			flex: 20,
		},
		{
			field: 'fax',
			headerName: t('field.faxNo'),
			flex: 20,
		},
		{
			field: 'email',
			headerName: t('field.email'),
			flex: 20,
		},
		{
			field: 'icName',
			headerName: t('field.nameIc'),
			flex: 20,
		},
		{
			field: 'updatedAt',
			headerName: t('field.lastUpdated'),
			flex: 20,
		},

		{
			field: 'status',
			headerName: t('field.status'),
			flex: 20,
			renderCell: (params) => (
				<Chip
					color={params.row.status === 0 ? 'chipTertiary' : 'chipPrimary'}
					sx={{
						height: 'auto',
						minHeight: '24px',
						maxWidth: '100%',
						overflow: 'hidden',
						whiteSpace: 'normal',
						wordBreak: 'break-word',
						padding: '4px 4px',
					}}
					label={
						<Box
							sx={{
								width: '100%',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
							}}
						>
							{params.row.status === 0
								? t('field.active')
								: t('field.inActive')}
						</Box>
					}
				/>
			),
		},
		{
			field: 'actionedByName',
			headerName: t('field.actionedBy'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<EditUnitDialog currentId={params.row.id} formType={FormType.edit} />
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('common.unit')}</Typography>
			</Box>
			<Box marginTop={1}>
				{userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementManageStatus
				) && (
					<Button
						endIcon={<DonutLargeOutlinedIcon />}
						onClick={() => {
							navigate(
								`/field-management/field-management-list/unit/${fieldId}/status`
							);
						}}
						variant='contained'
						sx={{ marginRight: '12px' }}
					>
						{t('field.status')}
					</Button>
				)}
				{userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementReOrder
				) && (
					<Button
						endIcon={<SwapVertOutlinedIcon />}
						onClick={() => {
							navigate(
								`/field-management/field-management-list/unit/${fieldId}/order`
							);
						}}
						variant='contained'
						sx={{ marginRight: '12px' }}
					>
						{t('field.reOrderOption')}
					</Button>
				)}

				<EditUnitDialog formType={FormType.create} />
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					type='submit'
					variant='contained'
					onClick={() => {
						navigate('/field-management/field-management-list');
					}}
				>
					{t('button.back')}
				</Button>
			</Box>
		</>
	);
}
